import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';

import { Button } from '@swyftx/aviary/atoms/Button';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Modal } from '@swyftx/aviary/atoms/Modal';
import { ModalProps } from '@swyftx/aviary/atoms/Modal/Modal.styles';
import { Body } from '@swyftx/aviary/atoms/Typography';
import { useSnackBars } from '@swyftx/aviary/complex/GlobalSnackBars/useSnackBars';
import { UpdateMarginPosition } from '@swyftx/margin/build/types';

import { DateTime } from 'luxon';
import { useMarginInstrumentDetails } from 'src/lib/margin/hooks/instruments';
import { useUpdateMarginPosition } from 'src/lib/margin/hooks/positions';
import { useMarginPositionDetails } from 'src/lib/margin/hooks/positions/useMarginPositionDetails';
import { MarginPositionDetailsStep } from 'src/lib/margin/types';

type Props = {
  positionId?: string;
  open?: boolean;
  onClose?: () => void;
  onRemove?: () => void;
  modalPosition?: ModalProps['position'];
};

const MarginPositionDetailsDeletePendingOrderModal: React.FC<PropsWithChildren<Props>> = ({
  positionId,
  open = false,
  modalPosition = 'dynamic',
  onClose,
  onRemove,
  children,
}) => {
  const [modalOpen, setModalOpen] = useState<boolean>(open);
  const { position, positionMarket } = useMarginPositionDetails({
    positionId,
  });
  const { symbol } = useMarginInstrumentDetails({ instrument: positionMarket });

  const [loading, setLoading] = useState<boolean>(false);
  const { updatePosition, invalidateCache } = useUpdateMarginPosition();
  const { addSnackBar } = useSnackBars();

  useEffect(() => {
    setModalOpen(open);
  }, [open]);

  const onUpdatePosition = useCallback(async () => {
    if (!position) return;

    try {
      setLoading(true);

      const positionUpdate: UpdateMarginPosition = {
        closed: DateTime.now().toMillis(),
      };

      await updatePosition(position.positionId, positionUpdate);

      addSnackBar({
        variant: 'success',
        content: `${symbol} ${position.sell ? 'short' : 'long'} position closed`,
        showIcon: true,
        duration: 5_000,
      });

      if (onRemove) onRemove();
    } catch (e) {
      addSnackBar({
        variant: 'error',
        content: `Failed to close ${symbol} position`,
        showIcon: true,
        duration: 5_000,
      });

      invalidateCache();
    } finally {
      setLoading(false);
      if (onClose) onClose();
    }
  }, [addSnackBar, invalidateCache, onClose, onRemove, position, symbol, updatePosition]);

  return (
    <Modal
      key={MarginPositionDetailsStep.ClosePosition}
      overlayClassName='z-modal-popover-backdrop'
      title='Delete order'
      position={modalPosition}
      className='sm:min-w-[400px] sm:max-w-[400px] md:min-w-[400px] md:max-w-[400px] lg:min-w-[400px] lg:max-w-[400px]'
      open={modalOpen}
      onOpenChange={(o) => setModalOpen(!!o)}
      triggerElement={children}
      onClose={onClose}
    >
      <FlexLayout direction='column' spacing={16} className='h-full px-16 pb-16' justifyContent='space-between'>
        <Body className='w-full ' color='secondary'>
          This action cannot be undone. Are you sure you would like to remove this order?
        </Body>
        <Button color='destructive' size='lg' loading={loading} onClick={onUpdatePosition}>
          Yes, delete
        </Button>
      </FlexLayout>
    </Modal>
  );
};

export { MarginPositionDetailsDeletePendingOrderModal };
